import { FIELDS as EN_US_FIELDS } from "./langs/en-US";
import { FIELDS as EN_GB_FIELDS } from "./langs/en-GB";
import { FIELDS as EN_AU_FIELDS } from "./langs/en-AU";
import { FIELDS as EN_CA_FIELDS } from "./langs/en-CA";
import { FIELDS as EN_IE_FIELDS } from "./langs/en-IE";
import { FIELDS as EN_NZ_FIELDS } from "./langs/en-NZ";
import { FIELDS as EN_ZA_FIELDS } from "./langs/en-ZA";
import { FIELDS as DE_DE_FIELDS } from "./langs/de-DE";
import { FIELDS as DE_AT_FIELDS } from "./langs/de-AT";
import { FIELDS as DE_BE_FIELDS } from "./langs/de-BE";
import { FIELDS as DE_LU_FIELDS } from "./langs/de-LU";
import { FIELDS as ES_ES_FIELDS } from "./langs/es-ES";
import { FIELDS as ES_AR_FIELDS } from "./langs/es-AR";
import { FIELDS as ES_MX_FIELDS } from "./langs/es-MX";
import { FIELDS as FR_FR_FIELDS } from "./langs/fr-FR";
import { FIELDS as FR_BE_FIELDS } from "./langs/fr-BE";
import { FIELDS as FR_CA_FIELDS } from "./langs/fr-CA";
import { FIELDS as FR_LU_FIELDS } from "./langs/fr-LU";
import { FIELDS as IT_IT_FIELDS } from "./langs/it-IT";
import { FIELDS as IT_MT_FIELDS } from "./langs/it-MT";
import { FIELDS as JA_JP_FIELDS } from "./langs/ja-JP";
import { FIELDS as NL_NL_FIELDS } from "./langs/nl-NL";
import { FIELDS as NL_BE_FIELDS } from "./langs/nl-BE";
import { FIELDS as PT_BR_FIELDS } from "./langs/pt-BR";
import { FIELDS as PT_PT_FIELDS } from "./langs/pt-PT";
import { FIELDS as BG_BG_FIELDS } from "./langs/bg-BG";
import { FIELDS as CS_CZ_FIELDS } from "./langs/cs-CZ";
import { FIELDS as DA_DK_FIELDS } from "./langs/da-DK";
import { FIELDS as EL_CY_FIELDS } from "./langs/el-CY";
import { FIELDS as EL_GR_FIELDS } from "./langs/el-GR";
import { FIELDS as ET_EE_FIELDS } from "./langs/et-EE";
import { FIELDS as FI_FI_FIELDS } from "./langs/fi-FI";
import { FIELDS as HR_HR_FIELDS } from "./langs/hr-HR";
import { FIELDS as HU_HU_FIELDS } from "./langs/hu-HU";
import { FIELDS as ID_ID_FIELDS } from "./langs/id-ID";
import { FIELDS as IS_IS_FIELDS } from "./langs/is-IS";
import { FIELDS as KO_KR_FIELDS } from "./langs/ko-KR";
import { FIELDS as LT_LT_FIELDS } from "./langs/lt-LT";
import { FIELDS as LV_LV_FIELDS } from "./langs/lv-LV";
import { FIELDS as MS_MY_FIELDS } from "./langs/ms-MY";
import { FIELDS as NO_NO_FIELDS } from "./langs/no-NO";
import { FIELDS as PL_PL_FIELDS } from "./langs/pl-PL";
import { FIELDS as RO_RO_FIELDS } from "./langs/ro-RO";
import { FIELDS as RU_RU_FIELDS } from "./langs/ru-RU";
import { FIELDS as RU_UA_FIELDS } from "./langs/ru-UA";
import { FIELDS as SK_SK_FIELDS } from "./langs/sk-SK";
import { FIELDS as SL_SI_FIELDS } from "./langs/sl-SI";
import { FIELDS as SV_SE_FIELDS } from "./langs/sv-SE";
import { FIELDS as TH_TH_FIELDS } from "./langs/th-TH";
import { FIELDS as TR_TR_FIELDS } from "./langs/tr-TR";
import { FIELDS as VI_VN_FIELDS } from "./langs/vi-VN";
import { FIELDS as ZH_CN_FIELDS } from "./langs/zh-CN";

type NestedPaths<T> = T extends object
  ? {
      [K in keyof T]: K extends string
        ? T[K] extends object
          ? `${K}.${NestedPaths<T[K]>}`
          : K
        : never;
    }[keyof T]
  : never;

export type LocaleKey = keyof typeof TRANSLATIONS;
export type TranslationPaths = NestedPaths<(typeof TRANSLATIONS)[LocaleKey]>;

/**
 * An object containing translations for different locales.
 */
const FALLBACK_LOCALE = "en-US" as const satisfies LocaleKey;

const TRANSLATIONS = {
  "en-US": {
    common: {
      example: {
        hello: "Hello World!",
      },
    },
    fields: EN_US_FIELDS,
  },
  "en-GB": {
    common: {
      example: {
        hello: "Hello World!",
      },
    },
    fields: EN_GB_FIELDS,
  },
  "en-AU": {
    common: {
      example: {
        hello: "Hello World!",
      },
    },
    fields: EN_AU_FIELDS,
  },
  "en-CA": {
    common: {
      example: {
        hello: "Hello World!",
      },
    },
    fields: EN_CA_FIELDS,
  },
  "en-IE": {
    common: {
      example: {
        hello: "Hello World!",
      },
    },
    fields: EN_IE_FIELDS,
  },
  "en-NZ": {
    common: {
      example: {
        hello: "Hello World!",
      },
    },
    fields: EN_NZ_FIELDS,
  },
  "en-ZA": {
    common: {
      example: {
        hello: "Hello World!",
      },
    },
    fields: EN_ZA_FIELDS,
  },
  "de-DE": {
    common: {
      example: {
        hello: "Hallo Welt!",
      },
    },
    fields: DE_DE_FIELDS,
  },
  "de-AT": {
    common: {
      example: {
        hello: "Hallo Welt!",
      },
    },
    fields: DE_AT_FIELDS,
  },
  "de-BE": {
    common: {
      example: {
        hello: "Hallo Welt!",
      },
    },
    fields: DE_BE_FIELDS,
  },
  "de-LU": {
    common: {
      example: {
        hello: "Hallo Welt!",
      },
    },
    fields: DE_LU_FIELDS,
  },
  "es-ES": {
    common: {
      example: {
        hello: "¡Hola Mundo!",
      },
    },
    fields: ES_ES_FIELDS,
  },
  "es-AR": {
    common: {
      example: {
        hello: "¡Hola Mundo!",
      },
    },
    fields: ES_AR_FIELDS,
  },
  "es-MX": {
    common: {
      example: {
        hello: "¡Hola Mundo!",
      },
    },
    fields: ES_MX_FIELDS,
  },
  "fr-FR": {
    common: {
      example: {
        hello: "Bonjour le monde!",
      },
    },
    fields: FR_FR_FIELDS,
  },
  "fr-BE": {
    common: {
      example: {
        hello: "Bonjour le monde!",
      },
    },
    fields: FR_BE_FIELDS,
  },
  "fr-CA": {
    common: {
      example: {
        hello: "Bonjour le monde!",
      },
    },
    fields: FR_CA_FIELDS,
  },
  "fr-LU": {
    common: {
      example: {
        hello: "Bonjour le monde!",
      },
    },
    fields: FR_LU_FIELDS,
  },
  "it-IT": {
    common: {
      example: {
        hello: "Ciao Mondo!",
      },
    },
    fields: IT_IT_FIELDS,
  },
  "it-MT": {
    common: {
      example: {
        hello: "Ciao Mondo!",
      },
    },
    fields: IT_MT_FIELDS,
  },
  "ja-JP": {
    common: {
      example: {
        hello: "こんにちは世界！",
      },
    },
    fields: JA_JP_FIELDS,
  },
  "nl-NL": {
    common: {
      example: {
        hello: "Hallo Wereld!",
      },
    },
    fields: NL_NL_FIELDS,
  },
  "nl-BE": {
    common: {
      example: {
        hello: "Hallo Wereld!",
      },
    },
    fields: NL_BE_FIELDS,
  },
  "pt-BR": {
    common: {
      example: {
        hello: "Olá Mundo!",
      },
    },
    fields: PT_BR_FIELDS,
  },
  "pt-PT": {
    common: {
      example: {
        hello: "Olá Mundo!",
      },
    },
    fields: PT_PT_FIELDS,
  },
  "bg-BG": {
    common: {
      example: {
        hello: "Здравей, свят!",
      },
    },
    fields: BG_BG_FIELDS,
  },
  "cs-CZ": {
    common: {
      example: {
        hello: "Ahoj světe!",
      },
    },
    fields: CS_CZ_FIELDS,
  },
  "da-DK": {
    common: {
      example: {
        hello: "Hej verden!",
      },
    },
    fields: DA_DK_FIELDS,
  },
  "el-CY": {
    common: {
      example: {
        hello: "Γεια σου κόσμε!",
      },
    },
    fields: EL_CY_FIELDS,
  },
  "el-GR": {
    common: {
      example: {
        hello: "Γεια σου κόσμε!",
      },
    },
    fields: EL_GR_FIELDS,
  },
  "et-EE": {
    common: {
      example: {
        hello: "Tere, maailm!",
      },
    },
    fields: ET_EE_FIELDS,
  },
  "fi-FI": {
    common: {
      example: {
        hello: "Hei maailma!",
      },
    },
    fields: FI_FI_FIELDS,
  },
  "hr-HR": {
    common: {
      example: {
        hello: "Pozdrav svijete!",
      },
    },
    fields: HR_HR_FIELDS,
  },
  "hu-HU": {
    common: {
      example: {
        hello: "Helló világ!",
      },
    },
    fields: HU_HU_FIELDS,
  },
  "id-ID": {
    common: {
      example: {
        hello: "Halo Dunia!",
      },
    },
    fields: ID_ID_FIELDS,
  },
  "is-IS": {
    common: {
      example: {
        hello: "Halló heimur!",
      },
    },
    fields: IS_IS_FIELDS,
  },
  "ko-KR": {
    common: {
      example: {
        hello: "안녕하세요 세계!",
      },
    },
    fields: KO_KR_FIELDS,
  },
  "lt-LT": {
    common: {
      example: {
        hello: "Labas pasauli!",
      },
    },
    fields: LT_LT_FIELDS,
  },
  "lv-LV": {
    common: {
      example: {
        hello: "Sveika, pasaule!",
      },
    },
    fields: LV_LV_FIELDS,
  },
  "ms-MY": {
    common: {
      example: {
        hello: "Hai dunia!",
      },
    },
    fields: MS_MY_FIELDS,
  },
  "no-NO": {
    common: {
      example: {
        hello: "Hei verden!",
      },
    },
    fields: NO_NO_FIELDS,
  },
  "pl-PL": {
    common: {
      example: {
        hello: "Witaj świecie!",
      },
    },
    fields: PL_PL_FIELDS,
  },
  "ro-RO": {
    common: {
      example: {
        hello: "Salut lume!",
      },
    },
    fields: RO_RO_FIELDS,
  },
  "ru-RU": {
    common: {
      example: {
        hello: "Привет, мир!",
      },
    },
    fields: RU_RU_FIELDS,
  },
  "ru-UA": {
    common: {
      example: {
        hello: "Привет, мир!",
      },
    },
    fields: RU_UA_FIELDS,
  },
  "sk-SK": {
    common: {
      example: {
        hello: "Ahoj svet!",
      },
    },
    fields: SK_SK_FIELDS,
  },
  "sl-SI": {
    common: {
      example: {
        hello: "Pozdravljen, svet!",
      },
    },
    fields: SL_SI_FIELDS,
  },
  "sv-SE": {
    common: {
      example: {
        hello: "Hej världen!",
      },
    },
    fields: SV_SE_FIELDS,
  },
  "th-TH": {
    common: {
      example: {
        hello: "สวัสดีชาวโลก!",
      },
    },
    fields: TH_TH_FIELDS,
  },
  "tr-TR": {
    common: {
      example: {
        hello: "Merhaba Dünya!",
      },
    },
    fields: TR_TR_FIELDS,
  },
  "vi-VN": {
    common: {
      example: {
        hello: "Xin chào thế giới!",
      },
    },
    fields: VI_VN_FIELDS,
  },
  "zh-CN": {
    common: {
      example: {
        hello: "你好，世界！",
      },
    },
    fields: ZH_CN_FIELDS,
  },
} as const;

export type CommonCopy = {
  errors: Record<string, string>;
};

type FieldDictionary = typeof EN_US_FIELDS;

export type FieldKey = keyof FieldDictionary;
export type FieldCopy = FieldDictionary[FieldKey];

export function getFieldDictionary(locale: LocaleKey): FieldDictionary {
  return (TRANSLATIONS[locale]?.fields ??
    TRANSLATIONS[FALLBACK_LOCALE].fields) as FieldDictionary;
}

export function getFieldCopy(locale: LocaleKey, field: FieldKey): FieldCopy {
  const dictionary = getFieldDictionary(locale);
  if (dictionary[field]) {
    return dictionary[field];
  }

  return TRANSLATIONS[FALLBACK_LOCALE].fields[field] as FieldCopy;
}

/**
 * An object containing translations for different locales.
 * Each locale key maps to its respective translation object.
 *
 * @returns An array of available locale keys from the translations object.
 */
export function getLocales() {
  return Object.keys(TRANSLATIONS) as LocaleKey[];
}

/**
 * Retrieves a translation string for the specified locale and translation path.
 *
 * @template T - The locale key type that extends LocaleKey
 * @param locale - The locale identifier (e.g., "en-US", "fr-FR")
 * @param path - The dot-separated path to the translation key (e.g., "common.buttons.save")
 * @returns The translated string, or falls back to en-US if available, or returns the path if no translation is found
 *
 * @example
 * ```typescript
 * const greeting = t("en-US", "common.greetings.hello");
 * const buttonText = t("fr-FR", "buttons.submit");
 * ```
 *
 * @remarks
 * - If the translation is not found in the requested locale, the function will attempt to fall back to "en-US"
 * - If no translation is found in either the requested locale or the fallback, a warning is logged and the path is returned
 * - Uses dynamic property access to traverse nested translation objects
 */
export function t<T extends LocaleKey>(
  locale: T,
  path: TranslationPaths,
): string {
  const keys = path.split(".");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Using any to allow dynamic access to nested properties
  let value: any = TRANSLATIONS[locale];

  for (const key of keys) {
    value = value[key];
    if (value === undefined) {
      // If not en-US, try fallback to en-US first
      if (locale !== "en-US") {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Using any to allow dynamic access to nested properties
        let fallbackValue: any = TRANSLATIONS["en-US"];
        for (const fallbackKey of keys) {
          fallbackValue = fallbackValue[fallbackKey];
          if (fallbackValue === undefined) {
            break;
          }
        }
        if (fallbackValue !== undefined) {
          return fallbackValue;
        }
      }

      console.warn(`Translation for "${path}" not found in locale "${locale}"`);
      return path; // Return the path if translation is not found
    }
  }

  return value;
}
